export type InterforumCompanyIdentifier = "ACMEDIA" | "ROSIEWOLF";

/** Headroom above typical 65MB Interforum EDI files. */
export const INTERFORUM_EDI_MAX_UPLOAD_BYTES = 70 * 1024 * 1024;

export const INTERFORUM_EDI_MAX_UPLOAD_MB = 70;

export const INTERFORUM_COMPANY_BY_ID: Partial<Record<number, InterforumCompanyIdentifier>> = {
    166: "ACMEDIA",
    142: "ROSIEWOLF",
};

export const INTERFORUM_EDI_FILE_NAME_PATTERN = /FPROD\.COM\.(ACMEDIA|ROSIEWOLF)\.EDI(100|350)/;

export const interforumEdiFileNameValidator = (file: File) => {
    if (!INTERFORUM_EDI_FILE_NAME_PATTERN.test(file.name)) {
        return {
            code: "invalid-interforum-filename",
            message: "Invalid Interforum EDI file name",
        };
    }
    return null;
};

/** Reads first EDI header line without loading the whole file (safe for ~65MB files). */
export const readEdiHeaderIdentifier = async (file: File): Promise<string> => {
    const headerChunk = file.slice(0, 8192);
    const text = await headerChunk.text();
    const firstLine = text.split(/\r?\n/)[0] ?? "";
    const identifier = firstLine.replace(/\s+/g, "");

    if (!identifier) {
        throw new Error("Empty EDI header");
    }

    return identifier;
};

const INTERFORUM_S3_UPLOAD_SOURCE = "interforum-manual";

type InterforumS3UploadHeaderContext = {
    companyId?: number;
    processingJobId?: number;
    accountId?: number;
};

const buildInterforumS3UploadHeadersFromJob = ({
    companyId,
    processingJobId,
    accountId,
}: Required<InterforumS3UploadHeaderContext>): Record<string, string> => ({
    "x-amz-meta-crealo-company-id": String(companyId),
    "x-amz-meta-crealo-processing-job-id": String(processingJobId),
    "x-amz-meta-crealo-uploaded-by": String(accountId),
    "x-amz-meta-crealo-source": INTERFORUM_S3_UPLOAD_SOURCE,
});

const parseInterforumS3UploadHeadersFromSignedUrl = (signedUrl: string): Record<string, string> => {
    const url = new URL(signedUrl);
    const headers: Record<string, string> = {};

    url.searchParams.forEach((value, key) => {
        if (key.toLowerCase().startsWith("x-amz-meta-")) {
            headers[key.toLowerCase()] = value;
        }
    });

    const signedHeadersParam =
        url.searchParams.get("X-Amz-SignedHeaders") ?? url.searchParams.get("x-amz-signedheaders") ?? "";
    const signedHeaders = decodeURIComponent(signedHeadersParam).toLowerCase().split(";");

    if (signedHeaders.includes("content-type")) {
        const contentType = url.searchParams.get("Content-Type") ?? url.searchParams.get("content-type");
        if (contentType) {
            headers["Content-Type"] = contentType;
        }
    }

    return headers;
};

/** Headers required for presigned PUT — must match X-Amz-SignedHeaders on the signed URL. */
export const buildInterforumS3UploadHeaders = (
    signedUrl: string,
    context: InterforumS3UploadHeaderContext = {},
): Record<string, string> => {
    const headersFromUrl = parseInterforumS3UploadHeadersFromSignedUrl(signedUrl);
    const hasMetadataHeaders = Object.keys(headersFromUrl).some((key) => key.startsWith("x-amz-meta-"));

    if (hasMetadataHeaders) {
        return headersFromUrl;
    }

    const { companyId, processingJobId, accountId } = context;
    if (companyId && processingJobId && accountId) {
        return buildInterforumS3UploadHeadersFromJob({ companyId, processingJobId, accountId });
    }

    return headersFromUrl;
};
